import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
    Table,
    Button,
    Flex,
    Breadcrumb,
    Form,
    message,
    Popconfirm,
    Space,
    Pagination,
    Switch,
    TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
    customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Enquiry {
    enquiry_id: number;
    enquiry_name: string;
    enquiry_email?: string;          // added email
    enquiry_mobile?: string;         // added mobile
    inquiry_description?: string;    // added inquiry description
    enquiry_status: number;
}

const GeneralEnquiry: React.FC = () => {
    const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [initialValues, setInitialValues] = useState<Enquiry | null>(null);
    const { styles } = useStyle();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const currentModule = useCurrentModule();

    const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
    const [sortedInfo, setSortedInfo] = useState<any>({});

    const cancel: PopconfirmProps['onCancel'] = (e) => {
        console.log(e);
        showError('Click on No');
    };

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const fetchEnquiries = async () => {
        try {
            const data = await apiRequest<Enquiry[]>({
                method: 'get',
                url: 'general-enquiry',
            });
            setEnquiries(data);
        } catch (error) {
            showError('Failed to fetch enquiries');
            console.error(error);
        }
    };

    useEffect(() => {
        fetchEnquiries();
    }, []);

    const handleEditClick = (record: Enquiry) => {
        setIsEditMode(true);
        setInitialValues({
            enquiry_id: record.enquiry_id,
            enquiry_name: record.enquiry_name,
            enquiry_status: record.enquiry_status,
        });
        setIsModalVisible(true);
    };

    const handleCreate = () => {
        fetchEnquiries();
    };

    const showCreateModal = () => {
        setIsEditMode(false);
        setInitialValues(null);
        setIsModalVisible(true);
    };

    const hideCreateModal = () => {
        setIsModalVisible(false);
        setIsEditMode(false);
        setInitialValues(null);
    };

    const handleDelete = async (enquiry_id: number) => {
        try {
            await apiRequest({
                method: 'delete',
                url: `/delete-general-enquiry/${enquiry_id}`,
            });
            showSuccess('Enquiry deleted');
            fetchEnquiries();
        } catch {
            showError('Failed to delete enquiry');
        }
    };

    const handleStatusToggle = async (enquiry_id: number) => {
        setEnquiries((prev) =>
            prev.map((item) =>
                item.enquiry_id === enquiry_id
                    ? { ...item, enquiry_status: item.enquiry_status === 1 ? 0 : 1 }
                    : item
            )
        );

        try {
            await apiRequest({
                method: 'put',
                url: `/update-status-general-enquiry/${enquiry_id}`,
            });
            showSuccess('Enquiry status updated');
        } catch (error) {
            showError('Failed to update status');
            setEnquiries((prev) =>
                prev.map((item) =>
                    item.enquiry_id === enquiry_id
                        ? { ...item, enquiry_status: item.enquiry_status === 1 ? 0 : 1 }
                        : item
                )
            );
        }
    };

    const handleTableChange: TableProps<Enquiry>['onChange'] = (pagination, filters, sorter) => {
        setFilteredInfo(filters);
        setSortedInfo(sorter);
    };

    const columns: TableColumnsType<Enquiry> = [
        {
            title: 'Enquiry',
            dataIndex: 'enquiry_name',
            key: 'enquiry_name',
            filters: Array.from(new Set(enquiries.map((s) => s.enquiry_name))).map((name) => ({
                text: name,
                value: name,
            })),
            filterSearch: true,
            filteredValue: filteredInfo.enquiry_name || null,
            onFilter: (value, record) => record.enquiry_name.includes(value as string),
            sorter: (a, b) => a.enquiry_name.localeCompare(b.enquiry_name),
            sortOrder: sortedInfo.columnKey === 'enquiry_name' ? sortedInfo.order : null,
            ellipsis: true,
            render: (_, record) => <div>{record.enquiry_name}</div>,
        },
        {
            title: 'Email',
            dataIndex: 'enquiry_email',
            key: 'enquiry_email',
            ellipsis: true,
            render: (email) => <div>{email || '-'}</div>,
        },
        {
            title: 'Mobile',
            dataIndex: 'enquiry_mobile',
            key: 'enquiry_mobile',
            ellipsis: true,
            render: (mobile) => <div>{mobile || '-'}</div>,
        },
        {
            title: 'Description',
            dataIndex: 'inquiry_description',
            key: 'inquiry_description',
            ellipsis: true,
            render: (desc) => <div>{desc || '-'}</div>,
        },
        // ... existing Action column if any
    ];

    if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
        columns.push({
            title: 'Action',
            width: 200,
            render: (_: any, record: Enquiry) => (
                <Space size="middle">
                    {currentModule?.right_edit === 'Y' && (
                        <>
                            <Switch
                                checkedChildren="Active"
                                unCheckedChildren="Inactive"
                                checked={!!record.enquiry_status}
                                onChange={() => handleStatusToggle(record.enquiry_id)}
                            />
                        </>
                    )}
                    {currentModule?.right_delete === 'Y' && (
                        <Popconfirm
                            title="Delete this enquiry?"
                            onConfirm={() => handleDelete(record.enquiry_id)}
                            onCancel={cancel}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button danger className="px-8">
                                <DeleteOutlined />
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        });
    }

    return (
        <>
            <Space
                style={{
                    display: 'flex',
                    marginBottom: 16,
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                }}
            >
                <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
                    <Breadcrumb.Item href="/">
                        <HomeOutlined />
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>View Enquiries</Breadcrumb.Item>
                </Breadcrumb>


            </Space>

            <div
                style={{
                    minHeight: 360,
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <Table<Enquiry>
                    tableLayout="auto"
                    className={`${styles.customTable} scrollable-table`}
                    columns={columns}
                    onChange={handleTableChange}
                    rowKey="enquiry_id"
                    dataSource={enquiries.slice(
                        (pagination.current - 1) * pagination.pageSize,
                        pagination.current * pagination.pageSize
                    )}
                    bordered
                    pagination={false}
                    scroll={{ y: 37 * 11 }}
                    locale={{ emptyText: 'No enquiry found' }}
                    footer={() => (
                        <Flex justify="space-between">
                            <span>
                                Showing {(pagination.current - 1) * pagination.pageSize + 1}–{Math.min(pagination.current * pagination.pageSize, enquiries.length)} of {enquiries.length}
                            </span>
                            <Pagination
                                {...pagination}
                                total={enquiries.length}
                                showSizeChanger
                                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
                            />
                        </Flex>
                    )}
                />
            </div>
        </>
    );
};

export default GeneralEnquiry;
